import {
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { DropdownModule } from 'primeng/dropdown';
import { PrimeTemplate } from 'primeng/api';
import { GeoDataStore } from '../../../stores/geo/geo-data.state';
import { HeaderComponent } from '../header/header.component';

@Component({
  selector: 'app-geo-group-dropdown',
  standalone: true,
  imports: [DropdownModule, PrimeTemplate, FormsModule, HeaderComponent],
  templateUrl: './geo-group-dropdown.component.html',
  styleUrl: './geo-group-dropdown.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GeoGroupDropdownComponent),
      multi: true,
    },
  ],
})
export class GeoGroupDropdownComponent
  implements ControlValueAccessor, OnInit, OnChanges
{
  @Input() headerStyleClass = '';
  @Input() styleClass = '';
  @Input() label = 'Gruppe';
  @Input() name = 'group';
  @Input() geoDataId!: string;
  @Input() selection = '';
  @Input() disabled = false;
  @Input() colSize: string | number = 12;
  @Input() enableEmpty = true;
  @Output() selectionChange = new EventEmitter<string>();
  readonly geoStore = inject(GeoDataStore);
  groups: string[] = [];
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  /**
   * Called when Angular detects changes to input properties bound to the component.
   * This lifecycle hook is used to respond to changes in component's inputs.
   *
   * @param {SimpleChanges} changes An object of the current and previous values of all of the component's bound input properties.
   * Properties can be checked for specific input changes to perform appropriate actions when inputs update.
   *
   * @return {void} This method does not return a value. It performs operations like updating component state or initiating specific logic.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['geoDataId']) {
      this.setGroups();
    }
  }

  /**
   * Angular lifecycle hook that is called after Angular has initialized
   * all data-bound properties of a directive.
   *
   * It invokes the `setGroups` method to initialize or set up any necessary
   * group-related logic or data for the component.
   *
   * @return {void} Does not return any value.
   */
  ngOnInit(): void {
    this.setGroups();
  }

  /**
   * Sets the groups property based on the geoDataId. If no groups are retrieved, resets the selection and triggers the selection change handler.
   *
   * @return {void} Does not return a value.
   */
  setGroups(): void {
    this.groups = this.geoStore.getGroupsById(this.geoDataId) || [];
    if (!this.groups.length) {
      this.selection = '';
      this.onSelectionChange('');
    }
  }

  /**
   * Called when the selection changes in the UI
   */
  onSelectionChange(value: string) {
    this.selection = value;
    this.onChange(value); // Notify the FormControl
    this.selectionChange.emit(value); // Emit custom event
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    this.selection = value;
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Optional: Handle disabled state
  }
}
